import { ArrowBack, ArrowForward } from 'assets/icons';
import Button from 'components/common/Button';
import ScrollToError from 'components/common/ScrollToError';
import NumberInput from 'components/inputs/NumberInput';
import { Field, Form, Formik, type FormikHelpers } from 'formik';
import { useCallback } from 'react';
import BankingImg from 'assets/images/banking.svg?react';
import { Col, Row } from 'react-flexbox-grid';
import { useIntl } from 'react-intl';
import SaveIcon from 'assets/icons/save.svg?react';
import SwitchInput from 'components/inputs/Switch';
import SliderWithTooltip from 'components/inputs/SliderWithTooltip';
import TextInput from 'components/inputs/TextInput';
import s from '../../calculators.module.scss';
import validationSchema from '../yupSchemas/buildHouseSecondStepSchema';
import type { IBuildHouseFormValues, IBuildHouseStepProps } from '../types';

const BuildHouseSecondStep = ({
  formValuesRef,
  isEditMode,
  lastStep,
  onSubmit,
  onSubmitStep,
  nextStep,
  previousStep,
  currentStep,
  onBack,
}: IBuildHouseStepProps) => {
  const { formatMessage: t } = useIntl();

  const onHasPropertyChange = useCallback(
    (
      value: boolean,
      name: string,
      setFieldValue: FormikHelpers<IBuildHouseFormValues>['setFieldValue'],
    ) => {
      setFieldValue(name, value);
      setFieldValue('estimated_property_price', null);
    },
    [],
  );

  return (
    <Formik<IBuildHouseFormValues>
      initialValues={formValuesRef?.current}
      onSubmit={async (values, formikActions) => {
        onSubmitStep(values);
        await onSubmit({ values, formikActions, currentStep, isEditMode, lastStep, nextStep });
      }}
      validationSchema={validationSchema}
    >
      {({ values, isSubmitting, setFieldValue }) => (
        <Row center="md" between="lg">
          <Col md={8} lg={6}>
            <Form>
              <ScrollToError />
              <Field
                name="full_address"
                component={TextInput}
                label={t({ id: 'project_wizard.full_address' })}
              />
              <Field
                name="zip"
                component={TextInput}
                label={t({ id: 'project_wizard.zip_code' })}
              />
              <Field
                name="land_area"
                component={SliderWithTooltip}
                min={200}
                step={10}
                max={3000}
                units="m²"
                label={t({ id: 'project_wizard.land_area' })}
              />
              <Field
                name="has_property"
                component={SwitchInput}
                label={t({ id: 'project_wizard.has_property' })}
                onChange={(v: boolean) => onHasPropertyChange(v, 'has_property', setFieldValue)}
              />
              {values.has_property ? (
                <Field
                  name="estimated_property_price"
                  component={NumberInput}
                  type="number"
                  min={0}
                  max={2000000}
                  label={t({ id: 'project_wizard.estimated_property_price' })}
                />
              ) : (
                <Field
                  label={t({ id: 'project_wizard.avg_property_price_sq_m' })}
                  name="avg_property_price_sq_m"
                  type="number"
                  component={NumberInput}
                  max={10000}
                />
              )}

              <div className={s.buttonsWrapper}>
                {!isEditMode ? (
                  <Button
                    disabled={isSubmitting}
                    onClick={() => onBack(values, previousStep)}
                    startIcon={<ArrowBack />}
                  >
                    {t({ id: 'app.back' })}
                  </Button>
                ) : null}
                <Button
                  type="submit"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                  endIcon={isEditMode ? <SaveIcon /> : <ArrowForward />}
                  className="ml-auto"
                >
                  {t({ id: `app.${isEditMode ? 'save' : 'submit_form_next'}` })}
                </Button>
              </div>
            </Form>
          </Col>

          <Col md={8} lg={6} first="xs" last="lg">
            <div className={s.imgBox}>
              <BankingImg
                title={t({ id: 'alt_text.project_details_page.new_building.second_step' })}
              />
            </div>
          </Col>
        </Row>
      )}
    </Formik>
  );
};

export default BuildHouseSecondStep;
